type McpPromptMessage = {
  role: string;
  content: unknown;
};

const extractMcpPromptContentText = (content: unknown): string | null => {
  if (typeof content === 'string') {
    return content;
  }

  if (typeof content !== 'object' || content === null) {
    return null;
  }

  const record = content as Record<string, unknown>;

  if (record.type === 'text' && typeof record.text === 'string') {
    return record.text;
  }

  if (record.type === 'image') {
    return '[image]';
  }

  if (record.type === 'resource') {
    const resource = record.resource;
    const resourceUri =
      typeof resource === 'object' &&
      resource !== null &&
      typeof (resource as { uri?: unknown }).uri === 'string'
        ? (resource as { uri: string }).uri
        : 'resource';
    return `[resource: ${resourceUri}]`;
  }

  if (Array.isArray(record.content)) {
    const parts = record.content
      .map((part) => extractMcpPromptContentText(part))
      .filter((part): part is string => part !== null);
    return parts.length > 0 ? parts.join('\n') : null;
  }

  return null;
};

export const formatMcpPromptTemplateBody = (
  messages: McpPromptMessage[],
): string | null => {
  if (messages.length === 0) {
    return null;
  }

  const formatted = messages
    .map((message) => {
      const text = extractMcpPromptContentText(message.content);
      if (!text) {
        return null;
      }

      if (messages.length === 1) {
        return text;
      }

      return `[${message.role}]\n${text}`;
    })
    .filter((line): line is string => line !== null);

  if (formatted.length === 0) {
    return null;
  }

  return formatted.join('\n\n');
};

export const normalizeMcpPromptTemplateBodyForDisplay = (
  templateBody: string,
): string => {
  const trimmed = templateBody.trim();
  if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) {
    return templateBody;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      const formatted = formatMcpPromptTemplateBody(
        parsed as McpPromptMessage[],
      );
      if (formatted) {
        return formatted;
      }
    }
  } catch {
    return templateBody;
  }

  return templateBody;
};
